/**
 * Rows the report screens show, from what the server sends: the trial
 * balance's pre-order tree with groups collapsed and zero rows hidden, and the
 * general ledger's pages merged into one list of account groups.
 */
import type { components } from "@linkhr/api-client/core";
import { ZERO } from "@linkhr/accounting-domain";

type S = components["schemas"];
export type TbRow = S["AcctTbRow"];
export type GlPage = S["AcctGeneralLedger"];
export type GlGroup = S["AcctGlGroup"];

/** Open items' sides, and what an item is per side (`AcctOpenItem.kind`), seeded EN and AR. */
export const SIDES = ["PAYABLE", "RECEIVABLE"] as const;
export type Side = (typeof SIDES)[number];
export const ITEM_KINDS = ["BILL", "CREDIT", "ADVANCE", "MANUAL"] as const;

/** A row's key: its account, or the computed prior-years row (which has none). */
export const rowKey = (row: TbRow): string => row.accountId ?? `computed:${row.code}`;

/** An absent or zero amount. */
export const zero = (amount: string | null | undefined): boolean =>
  amount === null || amount === undefined || ZERO.test(amount);

/** Every figure the row shows is zero (the comparison's too, when shown). */
export const isZeroRow = (row: TbRow, comparing: boolean): boolean =>
  [
    row.opening,
    row.debit,
    row.credit,
    row.closing,
    ...(comparing ? [row.previousClosing] : []),
  ].every(zero);

/**
 * The rows to show, in the server's pre-order: nothing under a collapsed
 * group, and with `hideZero` no row whose figures and whose whole subtree's
 * figures are zero (a group can net to zero over accounts that don't).
 */
export function visibleRows(
  rows: readonly TbRow[],
  collapsed: ReadonlySet<string>,
  hideZero: boolean,
  comparing: boolean,
): TbRow[] {
  const shown: TbRow[] = [];
  let hiddenBelow: number | null = null;
  rows.forEach((row, index) => {
    if (hiddenBelow !== null && row.depth > hiddenBelow) return;
    hiddenBelow = null;
    if (hideZero && subtreeIsZero(rows, index, comparing)) {
      hiddenBelow = row.depth;
      return;
    }
    shown.push(row);
    if (row.isGroup && collapsed.has(rowKey(row))) hiddenBelow = row.depth;
  });
  return shown;
}

function subtreeIsZero(rows: readonly TbRow[], index: number, comparing: boolean): boolean {
  const root = rows[index];
  if (root === undefined) return true;
  for (let i = index; i < rows.length; i += 1) {
    const row = rows[i] as TbRow;
    if (i > index && row.depth <= root.depth) break;
    if (!isZeroRow(row, comparing)) return false;
  }
  return true;
}

/**
 * The ledger's pages as one list of groups. A page starts with the group the
 * previous one ended in (the header repeats, with the rest of its lines), so
 * that group is extended rather than shown twice.
 */
export function mergeLedgerPages(pages: readonly GlPage[]): GlGroup[] {
  const groups: GlGroup[] = [];
  for (const page of pages) {
    for (const group of page.groups ?? []) {
      const last = groups[groups.length - 1];
      if (last !== undefined && last.account.id === group.account.id) {
        groups[groups.length - 1] = { ...last, lines: [...last.lines, ...group.lines] };
      } else {
        groups.push(group);
      }
    }
  }
  return groups;
}
